// Shared Zod schemas + types for the Avis supplier reference feed (round 1:
// countries, rental offices, car groups). These describe the RAW scraped
// catalog exactly as Avis exposes it — Avis-proprietary codes, transliterated
// city names, no coordinates. Intentionally permissive (a raw dump must never
// drop a row for being "too long"); normalization/validation happens at import.
import { z } from 'zod';

/** How an office row was discovered while scraping (for provenance + coverage). */
export const AvisOfficeSourceSchema = z.enum([
  'search-station', // <select name="SearchStation"> on the country page (has address)
  'select-city', // fallback: <select name="SelectCity"> (city-level, US umbrella)
  'dropoff-city', // fallback: <select name="SelectCity_2"> (drop-off only, e.g. GB Guernsey)
  'subdivision', // reached via a per-subdivision probe (US state / CA province)
]);
export type AvisOfficeSource = z.infer<typeof AvisOfficeSourceSchema>;

/** A country/subdivision option from the Avis <select name="Country"> list. */
export const AvisCountrySchema = z
  .object({
    /** Avis-proprietary code, stored verbatim: 'GB0', 'MA0', 'CAAB', 'USWY', 'AB0'. */
    code: z.string().trim().min(1),
    /** Avis-provided (often transliterated) name: 'United Kingdom Mainland', 'Canada Alberta'. */
    name: z.string().trim().min(1),
  })
  .strict();
export type AvisCountry = z.infer<typeof AvisCountrySchema>;

/** A rental office from <select name="SearchStation"> (or a city-level fallback). */
export const AvisOfficeSchema = z
  .object({
    /** The Avis country/subdivision code this office was fetched under. */
    countryCode: z.string().trim().min(1),
    /** 3-char Avis station code (proprietary, NOT IATA): 'LHR', 'S80', 'IW9'. */
    stationCode: z.string().trim().min(1),
    /** Transliterated city ('Fes', not 'Fès'); may itself contain a comma. */
    city: z.string().trim().default(''),
    /** Street address; frequently empty for airport counters. */
    address: z.string().trim().default(''),
    /** Verbatim scraped option text ('City; street-address'), for re-parse safety. */
    rawText: z.string().default(''),
    source: AvisOfficeSourceSchema,
    /** Set when reached via a per-subdivision probe (e.g. 'USWY', 'CAAB'). */
    subdivisionCode: z.string().trim().optional(),
    /** Only appeared in a drop-off list (pickup was empty), e.g. GB Guernsey -> GCI. */
    dropOffOnly: z.boolean().default(false),
  })
  .strict();
export type AvisOffice = z.infer<typeof AvisOfficeSchema>;

/** A car group A–P for a country, with an example vehicle. */
export const AvisCarGroupSchema = z
  .object({
    countryCode: z.string().trim().min(1),
    /** Single-letter Avis group code A..P (NOT ACRISS/SIPP). */
    groupCode: z.string().trim().min(1),
    /** Example vehicle string, e.g. 'Toyota Aygo X or similar'. May be empty. */
    example: z.string().trim().default(''),
  })
  .strict();
export type AvisCarGroup = z.infer<typeof AvisCarGroupSchema>;

/** Everything scraped for one Avis country: the country + its offices + its groups. */
export const AvisCountryBundleSchema = z
  .object({
    country: AvisCountrySchema,
    offices: z.array(AvisOfficeSchema).default([]),
    carGroups: z.array(AvisCarGroupSchema).default([]),
    /** Non-fatal notes for the audit report (zero offices, fallbacks used, etc.). */
    warnings: z.array(z.string()).default([]),
  })
  .strict();
export type AvisCountryBundle = z.infer<typeof AvisCountryBundleSchema>;

/** The top-level export the scraper writes and the importer reads. */
export const AvisExportSchema = z
  .object({
    /** ISO-8601 stamp of when the scrape ran (set by the caller, not the scraper). */
    generatedAt: z.string().default(''),
    countries: z.array(AvisCountrySchema),
    bundles: z.array(AvisCountryBundleSchema),
  })
  .strict();
export type AvisExport = z.infer<typeof AvisExportSchema>;

// ─── Live price quotes (Avis reservation flow) ───────────────────────

/** One priced car group from a ReservationStep2 page. */
export const AvisQuoteGroupSchema = z
  .object({
    /** Car group letter A-P. */
    group: z.string().length(1),
    /** Example vehicle ("Kia Picanto Auto"). */
    exampleCar: z.string(),
    /** Prepaid ("pay now") total for the whole rental span, native currency. */
    payNowTotal: z.number().nonnegative(),
    /** Pay-on-arrival total, if present. */
    payLaterTotal: z.number().nonnegative().nullable().default(null),
    /** ISO-4217 currency of the totals. */
    currency: z.string().length(3),
  })
  .strict();
export type AvisQuoteGroup = z.infer<typeof AvisQuoteGroupSchema>;

/** A full office quote: the group grid + the rental span it was priced for. */
export const AvisQuoteResultSchema = z
  .object({
    groups: z.array(AvisQuoteGroupSchema),
    days: z.number().int().positive(),
  })
  .strict();
export type AvisQuoteResult = z.infer<typeof AvisQuoteResultSchema>;
